import { ALLOWED_FILE_TYPES, UPLOAD_ERROR_MAP, DELETE_ATTACHMENT_CLASS } from '../constants';
import { UploadError } from '../typings';

import Ajax from '../lib/ajax';
import Toast from './toast-notification';
import translate from '../helpers/translate';

export interface Attachment {
    attachmentId: number;
    fileName: string; // Original upload name
    fileRealName: string; // Hashed file name
    fileSize: number;
    randID?: number;
}

let attachmentIdx = 1000; // Bigger then allowed from server, this means we don't need to recalculate idx on page load.

export function uploadAttachment(url: string, formData: FormData): Promise<Attachment[]> {
    const request = new Ajax();

    return request
        .postFiles(url, formData)
        .then((response: Response) => {
            return response.json();
        })
        .then((body: Object) => {
            const attachments = [];
            for (const [key, value] of Object.entries(body)) {
                let okReturn: [number, string, string, number, number?];
                let error: UploadError;

                const idx = attachmentIdx++;

                if (value[0] === 0) {
                    okReturn = value;

                    const attachment = {
                        attachmentId: idx,
                        fileName: okReturn[1],
                        fileRealName: okReturn[2],
                        fileSize: okReturn[3],
                        randID: okReturn[4],
                    };

                    attachments.push(attachment);
                } else {
                    error = value;

                    throw error;
                }
            }

            return attachments;
        });
}

export function attachEventAttachmentDelete() {
    const attachmentsList: HTMLDivElement | null = document.querySelector('#attachlist');
    attachmentsList?.addEventListener('click', (e: Event) => {
        const target: EventTarget | null = e.target;
        if (!(target instanceof HTMLElement && target.closest(DELETE_ATTACHMENT_CLASS) && target.closest(DELETE_ATTACHMENT_CLASS) instanceof HTMLButtonElement)) {
            return;
        }

        const idxElm = target.closest(DELETE_ATTACHMENT_CLASS);
        if (!(idxElm instanceof HTMLElement)) {
            return;
        }

        const idx = idxElm.dataset.attachment_id;
        const attachmentsWrapperDiv: HTMLDivElement | null = document.querySelector(`#attachments-${idx}`);
        const parentEl: HTMLElement | null | undefined = attachmentsWrapperDiv?.parentElement;
        const liEl: HTMLElement | null | undefined = target.closest(DELETE_ATTACHMENT_CLASS)?.parentElement;
        const olEl: HTMLElement | null | undefined = liEl?.parentElement;

        if (attachmentsWrapperDiv && olEl && liEl) {
            parentEl?.removeChild(attachmentsWrapperDiv);
            olEl.removeChild(liEl);
            updateAttachedCount();
        }

        const addAttachButton: HTMLButtonElement | null = document.querySelector('#addAttachButton');
        if (addAttachButton) {
            addAttachButton.classList.remove('btn--disabled');
            addAttachButton.disabled = false;
        }
    });
}

export function buildAttachmentInputs(attachment: Attachment): HTMLDivElement {
    const fileNameInput: HTMLInputElement = document.createElement('input');
    const fileRealNameInput: HTMLInputElement = document.createElement('input');
    const fileSizeInput: HTMLInputElement = document.createElement('input');
    const attachmentWrapperDiv: HTMLDivElement = document.createElement('div');
    const idx: number = attachment.attachmentId;

    attachmentWrapperDiv.id = `attachments-${idx}`;

    fileNameInput.name = `attachments[${idx}][0]`;
    fileSizeInput.name = `attachments[${idx}][1]`;
    fileRealNameInput.name = `attachments[${idx}][2]`;

    fileNameInput.value = attachment.fileName;
    fileSizeInput.value = `${attachment.fileSize}`;
    fileRealNameInput.value = attachment.fileRealName;

    [fileNameInput, fileSizeInput, fileRealNameInput].forEach((input: HTMLInputElement) => {
        return (input.type = 'hidden');
    });
    [fileNameInput, fileSizeInput, fileRealNameInput].forEach((input: HTMLInputElement) => {
        return attachmentWrapperDiv.appendChild(input);
    });

    return attachmentWrapperDiv;
}

export function buildAttachmentListItem(attachment: Attachment): HTMLLIElement {
    const idx = attachment.attachmentId;

    const attachmentItem: HTMLLIElement = document.createElement('li');
    attachmentItem.classList.add('grid');

    const attachmentContent: HTMLSpanElement = document.createElement('span');
    attachmentContent.innerText = attachment.fileName;
    attachmentContent.classList.add('attachment__filename');

    attachmentItem.appendChild(attachmentContent);

    const attachmentDeleteButton: HTMLButtonElement = document.createElement('button');
    attachmentDeleteButton.type = 'button';
    attachmentDeleteButton.className = 'attachment__delete js-attachment__delete';
    attachmentDeleteButton.setAttribute('data-attachment_id', `${idx}`);
    attachmentDeleteButton.setAttribute('data-tooltip', 'Delete attachment?');
    attachmentDeleteButton.setAttribute('data-tooltip-pos', 'down');

    const attachmentDeleteButtonIcon: HTMLElement = document.createElement('i');
    attachmentDeleteButtonIcon.className = 'icon icon--delete icon--sm';
    attachmentDeleteButtonIcon.setAttribute('aria-hidden', 'true');

    attachmentDeleteButton.appendChild(attachmentDeleteButtonIcon);
    attachmentItem.appendChild(attachmentDeleteButton);
    return attachmentItem;
}

export interface FileUploadReturns {
    form: HTMLFormElement;
    input: HTMLInputElement;
}

export function buildFileuploadForm(type: string): FileUploadReturns {
    const form: HTMLFormElement = document.createElement('form');
    const fileInput: HTMLInputElement = document.createElement('input');
    const typeInput: HTMLInputElement = document.createElement('input');

    form.method = 'post';
    form.action = '/ns/uploadattachment.html';

    fileInput.type = 'file';
    fileInput.accept = ALLOWED_FILE_TYPES;
    fileInput.name = 'attachment[]';

    typeInput.type = 'hidden';
    typeInput.name = 'typeId';
    typeInput.value = type;

    form.appendChild(typeInput);
    form.appendChild(fileInput);

    return {
        form,
        input: fileInput,
    };
}

function updateAttachedCount() {
    const attachedCount = document.querySelector<HTMLInputElement>('#countAttached');
    const attachmentsDiv = document.querySelector<HTMLDivElement>('#attachments');
    const len: number | undefined = attachmentsDiv?.childElementCount;

    if (attachedCount && len) {
        attachedCount.value = `${len}`;
    }

    return len;
}

export function buildAndAppendAttachments(attachmentsDiv: HTMLDivElement | null, attachlist: HTMLOListElement | null, attachments: Attachment[]) {
    attachments.forEach((attachment: Attachment) => {
        const attachmentsWrapper: HTMLElement = buildAttachmentInputs(attachment);
        const attachmentListItem: HTMLLIElement = buildAttachmentListItem(attachment);

        attachlist?.appendChild(attachmentListItem);
        attachmentsDiv?.appendChild(attachmentsWrapper);
    });

    toggleUploadProgress();
}

export function toggleUploadProgress(): void {
    const progress: HTMLLIElement | null = document.querySelector('.js-attachment__progress');

    if (progress) {
        progress.classList.toggle('hlp--hidden');
    }
}

export function buildAttachmentUpload(type: string, maxFiles: number, wrapperDiv: HTMLDivElement): HTMLInputElement {
    const attachmentsDiv = document.querySelector<HTMLDivElement>('#attachments');
    const attachlist = document.querySelector<HTMLOListElement>('#attachlist');
    const attachedCount = document.querySelector<HTMLInputElement>('#countAttached');
    const attachedLength: number = attachedCount ? parseInt(attachedCount.value, 10) : 0;
    const { form, input: fileInput }: FileUploadReturns = buildFileuploadForm(type);

    if (maxFiles - attachedLength > 1) {
        fileInput.multiple = true;
    } else {
        fileInput.multiple = false;
    }

    fileInput.addEventListener('change', function (this: HTMLInputElement, e: Event): any {
        const files = (<HTMLInputElement>e.target).files || [];

        if (files.length + attachedLength > 3) {
            e.stopPropagation();

            Toast.show({
                message: translate(802),
                type: 'warning',
            });

            wrapperDiv.removeChild(form);
        }
    });

    form.addEventListener('change', function (this: HTMLFormElement, e: Event) {
        const formData = new FormData(this);

        fileInput.disabled = true;

        toggleUploadProgress();

        uploadAttachment(form.action, formData)
            .then((newAttachments: Array<Attachment>) => {
                buildAndAppendAttachments(attachmentsDiv, attachlist, newAttachments);

                Toast.show({
                    message: translate([804, newAttachments[0].fileName]),
                    type: 'success',
                });
            })
            .then(updateAttachedCount)
            .then((totalUploaded: number) => {
                if (totalUploaded >= maxFiles) {
                    const addAttachButton = document.querySelector<HTMLButtonElement>('#addAttachButton');
                    addAttachButton?.classList.add('btn--disabled');
                    addAttachButton?.setAttribute('disabled', 'disabled');
                }
            })
            .then(() => {
                wrapperDiv.removeChild(form);
            })
            .catch((error: UploadError) => {
                toggleUploadProgress();

                Toast.show({
                    message: translate(UPLOAD_ERROR_MAP[error[0]]),
                    type: 'error',
                });
            });
    });

    wrapperDiv.appendChild(form);

    return fileInput;
}
